import React, { createContext, IframeHTMLAttributes, ReactNode, useContext, useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogTitle } from '../components/Common/UI/Dialog';
import Icons from '../components/Common/Icons/Icons';
import { Button } from '../components/Common/UI/Button';

interface PdfViewerTriggerProps extends IframeHTMLAttributes<HTMLIFrameElement>, React.PropsWithChildren {
  src: string;
  modalTitle?: string;
}
export function PdfViewerTrigger({ src, modalTitle, children, ...rest }: PdfViewerTriggerProps) {
  const { handleOpen } = usePdfDialogProvider();

  return (
    <Button
      size="auto"
      variant="ghost"
      asChild
      onClick={() => handleOpen({ open: true, iframeProps: { src, ...rest }, modalTitle })}
    >
      {children}
    </Button>
  );
}

interface HandleModalProps {
  open: boolean;
  iframeProps: IframeHTMLAttributes<HTMLIFrameElement>;
  modalTitle?: string;
}

interface PdfDialogProviderProps {
  isOpen: boolean;
  handleOpen: ({ open, iframeProps }: HandleModalProps) => void;
}

const defaultValue: PdfDialogProviderProps = {
  isOpen: false,
  handleOpen: () => undefined,
};

const defaultIframeProps: IframeHTMLAttributes<HTMLIFrameElement> = {
  title: 'pdf-viewer',
  width: '100%',
  height: '625px',
};

const defaultModalTitle = 'Pdf viewer';

export const PdfDialogContext = createContext<PdfDialogProviderProps>(defaultValue);

function PdfDialogProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState<boolean>(defaultValue.isOpen);
  const [iframeParams, setIframeParams] = useState<IframeHTMLAttributes<HTMLIFrameElement>>({
    ...defaultIframeProps,
  });
  const [modalTitleState, setModalTitleState] = useState(defaultModalTitle);

  const handleOpen = ({ open, iframeProps, modalTitle = defaultModalTitle }: HandleModalProps) => {
    setIsOpen(open);
    setIframeParams({ ...defaultIframeProps, ...iframeProps });
    setModalTitleState(modalTitle);
  };

  const memoizedValue: PdfDialogProviderProps = useMemo(
    () => ({
      isOpen,
      handleOpen,
      iframeParams,
    }),
    [isOpen, handleOpen, iframeParams],
  );

  return (
    <PdfDialogContext.Provider value={memoizedValue}>
      <Dialog open={isOpen} onOpenChange={open => setIsOpen(open)}>
        <DialogContent className="max-h-[98%] max-w-7xl z-[1000]">
          <DialogTitle className="flex items-center gap-4 mb-[10px]">
            <Icons.AccountsFilled className="size-[34px]" />
            <div>
              <p className="text-xl leading-none">{modalTitleState || defaultModalTitle}</p>
            </div>
          </DialogTitle>

          <div id="modal-content">
            <iframe
              title="pdf-viewer"
              width="100%"
              // height="625px"
              className="min-h-[570px] max-h-[625px]"
              {...iframeParams}
            />
          </div>
        </DialogContent>
      </Dialog>
      {children}
    </PdfDialogContext.Provider>
  );
}

export default PdfDialogProvider;

export const usePdfDialogProvider = (): PdfDialogProviderProps => useContext(PdfDialogContext);
